import { useState } from "react";
import { IoCopyOutline } from "react-icons/io5";
import { FaCheck } from "react-icons/fa";
import { Button } from "components/common/buttons/Button";
import { Tooltip, TooltipTrigger, TooltipContent } from "components/common/tooltips/Tooltip";
import type { CopyableTextProps } from "./CopyableText.types";

export const CopyableText = ({
  text,
  className = "",
  buttonClassName = "",
  showCopyButton = true,
  onCopy,
  children,
  copyValue,
  align = "center",
}: CopyableTextProps) => {
  const [copied, setCopied] = useState(false);
  const valueToCopy = copyValue || text || (typeof children === "string" ? children : "");

  const handleCopy = async () => {
    if (!valueToCopy) return;
    try {
      await navigator.clipboard.writeText(valueToCopy);
      setCopied(true);
      onCopy?.(valueToCopy);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy text:", error);
    }
  };

  if (!valueToCopy) return <>{children}</>;

  // Check if className contains flex-col to determine layout
  const isColumnLayout = className.includes("flex-col");

  // Get alignment classes
  const getAlignClass = () => {
    switch (align) {
      case "left":
      case "start":
        return isColumnLayout ? "items-start text-left" : "items-start text-left";
      case "right":
      case "end":
        return isColumnLayout ? "items-end text-right" : "items-end text-right";
      case "center":
      default:
        return isColumnLayout ? "items-center text-center" : "items-center text-center";
    }
  };

  const alignClass = getAlignClass();
  const baseClasses = isColumnLayout
    ? `flex flex-col ${alignClass} group/copyable ${className}`
    : `flex ${alignClass} gap-1.5 group/copyable ${className}`;

  return (
    <div className={baseClasses}>
      <Tooltip placement="top" open={copied ? true : undefined}>
        <TooltipTrigger asChild>
          <button
            onClick={handleCopy}
            className={`text-inherit hover:text-foreground transition-colors cursor-pointer w-full ${isColumnLayout ? "" : "truncate min-w-0"} ${align === "left" || align === "start" ? "text-left" : align === "right" || align === "end" ? "text-right" : "text-center"} ${buttonClassName}`}
            aria-label="Copy"
          >
            {children || text}
          </button>
        </TooltipTrigger>
        <TooltipContent>{copied ? "Copied!" : "Click to copy"}</TooltipContent>
      </Tooltip>
      {showCopyButton && !isColumnLayout && (
        <Button
          type="button"
          color="gray"
          size="icon"
          onClick={handleCopy}
          className="h-6 w-6 shrink-0 rounded opacity-0 shadow-lg group-hover/copyable:opacity-100 [&_svg]:h-3 [&_svg]:w-3"
          aria-label="Copy"
        >
          {copied ? (
            <FaCheck className="text-success" />
          ) : (
            <IoCopyOutline className="text-muted-foreground hover:text-foreground/80" />
          )}
        </Button>
      )}
    </div>
  );
};
